import i18n from "i18next";
import { Trans } from "react-i18next";
/**
 * The Lilia menu — the switcher, with search inside it (navigation handoff
 * §2, drawing 1b). How the Windows menu evolved: the all-programs list became
 * pinned tiles and recents, and search merged into the menu.
 *
 * One panel, two ways in. Clicking **L** opens it on pinned and recent; **⌘K**,
 * or the bar's search field, opens the same panel already typing. Search is
 * deterministic retrieval, never Ask Lilia.
 *
 * Top to bottom: search · **Home** (its own row) · **Pinned** tools, eight
 * tiles (drag to reorder, right-click to unpin, the current tool highlighted)
 * and **All tools ›** · **Recent**, six items across tools · a footer with
 * Settings, Trash and Help. While typing, the body becomes results grouped
 * Tools, then per tool, then Actions; empty groups hide; arrows move, ↵ opens.
 *
 * Closing puts focus back where it was, so Esc returns the caret mid-word.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  IconHome, IconSearch, IconSettings, IconTrash, IconHelpCircle, IconPin, IconPinnedOff,
  IconChevronLeft, IconFileText, IconTable, IconRoute,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useApiContext, API_BASE } from "@/contexts/ApiContext";
import { useTrashDocuments } from "@/hooks/useDocuments";
import { api } from "@/lib/api";
import { paths } from "@/lib/addresses";
import { TOOLS, toolByKey, toolForPath, type ToolDef, type ToolKey } from "@/lib/tools";
import { acrossTools, itemHref, relWhen, useRecent } from "@/lib/recent";
import { movePinned, usePinnedTools } from "@/lib/pinnedTools";
import { toolEntry } from "@/lib/viewMemory";
import { useGlobalCommandActions } from "./useGlobalCommandActions";
import type { LiliaMenuMode } from "./liliaMenuStore";
import { SearchHitText } from "@/components/lilia-ui/search-hit-mark";
import { testIds } from "@/testIds";
import { usePlan } from "@/hooks/usePlan";
import { requestTour } from "@/components/onboarding/Tour";
import { cn } from "@/lib/cn";

interface LauncherProps {
  open: boolean;
  onClose: () => void;
  /** `search` opens with the cursor in the search field (⌘K). */
  mode?: LiliaMenuMode;
}

export function Launcher({ open, onClose, mode = "browse" }: LauncherProps) {
  if (!open) return null;
  return <LiliaMenuPanel onClose={onClose} mode={mode} />;
}

/** One row of results while typing. */
interface Result {
  id: string;
  kind: "tool" | "item" | "action";
  label: string;
  hint?: string;
  icon: ReactNode;
  run: () => void;
}

// Tiny fuzzy score, as the old palette had: substring first, then in order.
function score(hay: string, needle: string): number {
  const h = hay.toLowerCase();
  const n = needle.toLowerCase().trim();
  if (!n) return 0;
  if (h.startsWith(n)) return 200;
  if (h.split(/[\s/·-]+/).some((w) => w.startsWith(n))) return 150;
  if (h.includes(n)) return 100;
  let i = 0;
  for (const ch of h) if (ch === n[i]) i += 1;
  return i === n.length ? 10 : 0;
}

const Glyph = ({ tool, on, size = 30 }: { tool: ToolDef; on?: boolean; size?: number }) => {
  const Icon = tool.icon;
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center",
        on ? "bg-[var(--lilia-primary)] text-[color:var(--lilia-on-primary)]" : "bg-[var(--lilia-primary-subtle)] text-[var(--lilia-primary)]",
      )}
      style={{ width: size, height: size, borderRadius: size >= 30 ? 8 : 7 }}
    >
      <Icon size={Math.round(size * 0.53)} />
    </span>
  );
};

function LiliaMenuPanel({ onClose, mode }: { onClose: () => void; mode: LiliaMenuMode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { fetchWithAuth } = useApiContext();
  const current = toolForPath(pathname)?.key;

  const [q, setQ] = useState("");
  const [page, setPage] = useState<"main" | "all">("main");
  const [active, setActive] = useState(0);
  const typing = q.trim().length > 0;

  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus: in the search field for ⌘K, on the panel for L. Closing puts it
  // back where it was — the caret in a paper included.
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    if (mode === "search") inputRef.current?.focus();
    else panelRef.current?.querySelector<HTMLElement>("[data-menu-item]")?.focus();
    return () => {
      if (before && document.contains(before)) before.focus({ preventScroll: true });
    };
  }, [mode]);

  const go = (to: string) => {
    onClose();
    if (/^https?:/.test(to)) window.open(to, "_blank", "noopener");
    else navigate(to);
  };
  const openTool = (t: ToolDef) => go(toolEntry(t.key, t.path));

  // ── data ──
  const { pinned, setPinned } = usePinnedTools();
  const recentQ = useRecent(6);
  const recent = useMemo(() => acrossTools(recentQ.data ?? [], 6), [recentQ.data]);

  // Help → Take the tour (Olivia, 21 Oct): on this document; else the latest one when Free is
  // full (never the at-3 prompt); else a new blank one.
  const plan = usePlan().data;
  const inDocument = /^\/d\//.test(pathname);
  const docLimit = plan?.enforced ? plan.documents?.limit ?? 0 : 0;
  const latestDoc = recent.find((r) => r.tool === "documents");
  const atLimit = docLimit > 0 && (plan?.documents?.used ?? 0) >= docLimit && !!latestDoc;
  const tourWhere = inDocument ? "onThisDocument" : atLimit ? "onLatest" : "inNewDocument";
  const takeTour = async () => {
    onClose();
    if (inDocument) { requestTour(); return; }
    if (atLimit && latestDoc) { navigate(`/d/${latestDoc.id}?tour=1`); return; }
    try {
      const { document } = await api.documents.create({ title: i18n.t("common:untitledDocument") });
      navigate(`/d/${document.id}?tour=1`);
    } catch { /* the plan's limit dialog explains */ }
  };
  const { totalCount: trashCount } = useTrashDocuments(1, 1);
  const actions = useGlobalCommandActions();

  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q.trim()), 150);
    return () => window.clearTimeout(t);
  }, [q]);
  const docsQ = useQuery({
    queryKey: ["lilia-menu", "documents", debounced],
    enabled: debounced.length > 0,
    staleTime: 10_000,
    queryFn: () => api.documents.search(debounced, 6),
  });
  const tablesQ = useQuery({
    queryKey: ["lilia-menu", "tables"],
    enabled: typing,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await fetchWithAuth(`${API_BASE}/tables?limit=200`);
      return res.ok ? ((await res.json()) as { id: string; caption: string; label: string }[]) : [];
    },
  });

  // ── results while typing: Tools, then per tool, then Actions ──
  const groups = useMemo(() => {
    if (!typing) return [];
    const needle = q.trim();
    const rank = <T,>(xs: T[], text: (x: T) => string) =>
      xs.map((x) => ({ x, s: score(text(x), needle) })).filter((r) => r.s > 0).sort((a, b) => b.s - a.s).map((r) => r.x);

    const places: Result[] = [
      { id: "home", kind: "tool", label: i18n.t("shell:home.label"), hint: i18n.t("shell:menu.homeHint"), icon: <IconHome size={15} />, run: () => go(paths.home) },
      ...TOOLS.map((t) => ({ id: `tool-${t.key}`, kind: "tool" as const, label: t.name, hint: t.desc, icon: <t.icon size={15} />, run: () => openTool(t) })),
      { id: "trash", kind: "tool", label: i18n.t("shell:menu.trash"), hint: i18n.t("shell:menu.trashHint"), icon: <IconTrash size={15} />, run: () => go(paths.trash) },
      { id: "help", kind: "tool", label: i18n.t("shell:menu.help"), hint: i18n.t("shell:menu.helpHint"), icon: <IconHelpCircle size={15} />, run: () => go(paths.help()) },
    ];
    const tools = rank(places, (r) => r.label).slice(0, 5);
    const docs: Result[] = (docsQ.data ?? []).map((d) => ({
      id: `doc-${d.id}`, kind: "item", label: d.title || i18n.t("shell:bar.untitled"), hint: i18n.t("shell:menu.openDocument"),
      icon: <IconFileText size={15} />, run: () => go(paths.doc(d.id)),
    }));
    const tables: Result[] = rank(tablesQ.data ?? [], (t) => `${t.caption} ${t.label}`).slice(0, 5).map((t) => ({
      id: `table-${t.id}`, kind: "item", label: t.caption || t.label || i18n.t("shell:menu.untitledTable"), hint: t.label && t.caption ? t.label : i18n.t("shell:menu.openTable"),
      icon: <IconTable size={15} />, run: () => go(paths.table(t.id)),
    }));
    const acts: Result[] = rank(actions.filter((a) => !a.disabled), (a) => `${a.label} ${a.hint ?? ""}`).slice(0, 6).map((a) => ({
      id: a.id, kind: "action", label: a.label, hint: a.hint, icon: a.icon, run: () => { onClose(); a.run(); },
    }));
    return [
      { id: "tools", label: i18n.t("shell:menu.groups.tools"), items: tools },
      { id: "documents", label: i18n.t("shell:menu.groups.documents"), items: docs },
      { id: "tables", label: i18n.t("shell:menu.groups.tables"), items: tables },
      { id: "actions", label: i18n.t("shell:menu.groups.actions"), items: acts },
    ].filter((g) => g.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typing, q, docsQ.data, tablesQ.data, actions]);
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  useEffect(() => setActive(0), [q]);

  // ── keys ──
  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      if (page === "all") setPage("main");
      else onClose();
      return;
    }
    if (typing) {
      if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(flat.length - 1, i + 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
      else if (e.key === "Enter") { e.preventDefault(); flat[active]?.run(); }
      return;
    }
    // Browsing: arrows walk the menu's items in reading order; ↵ is the
    // focused button's own click.
    const dir = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (!dir) return;
    if (document.activeElement === inputRef.current && (e.key === "ArrowLeft" || e.key === "ArrowRight")) return;
    const items = [...(panelRef.current?.querySelectorAll<HTMLElement>("[data-menu-item]") ?? [])];
    if (items.length === 0) return;
    e.preventDefault();
    const at = items.indexOf(document.activeElement as HTMLElement);
    const step = (e.key === "ArrowDown" || e.key === "ArrowUp") && (document.activeElement as HTMLElement)?.dataset.menuTile ? 4 * dir : dir;
    items[Math.max(0, Math.min(items.length - 1, at < 0 ? 0 : at + step))]?.focus();
  };

  // ── pinned: drag to reorder, right-click to unpin ──
  const dragging = useRef<ToolKey | null>(null);
  const pinnedTools = pinned.map((k) => toolByKey(k)).filter((t): t is ToolDef => !!t);
  const togglePin = (key: ToolKey) =>
    setPinned(pinned.includes(key) ? pinned.filter((k) => k !== key) : [...pinned, key]);

  const itemCls = "outline-none focus-visible:ring-2 focus-visible:ring-[var(--lilia-primary)]";
  const sectionHead = "text-[10.5px] font-bold uppercase tracking-[.07em] text-[var(--lilia-text-muted)]";

  return (
    <>
      <div
        data-testid={testIds.liliaMenu.scrim}
        onClick={onClose}
        // From the bar's bottom edge down, wherever the bar sits (a banner
        // can push it down): the bar itself stays usable.
        className="absolute left-0 top-full z-40 h-[100dvh] w-screen"
        style={{ background: "rgba(15, 22, 32, 0.18)" }}
      />
      <div
        ref={panelRef}
        data-testid={testIds.liliaMenu.root}
        data-mode={typing ? "search" : page === "all" ? "all-tools" : "browse"}
        role="dialog"
        aria-label={i18n.t("shell:menu.label")}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="lilia-launcher-pop absolute z-[41] overflow-hidden rounded-[14px] border border-[var(--lilia-border-strong,var(--lilia-border))] bg-[var(--lilia-surface)] shadow-[0_24px_60px_rgba(16,24,40,0.24)]"
        style={{ top: 54, left: 10, width: 620, maxWidth: "calc(100vw - 20px)" }}
      >
        {/* search */}
        <div className="px-3.5 pb-2.5 pt-3.5">
          {/* The field's drawn ring (1b) is its focus ring: it's always
              focused while the menu is typing, so the global outline would
              only draw a second box inside it. */}
          <label className="flex h-10 items-center gap-[9px] rounded-[9px] border border-[var(--lilia-primary)] bg-[var(--lilia-bg)] px-3 shadow-[0_0_0_3px_var(--lilia-primary-subtle)]">
            <IconSearch size={16} className="shrink-0 text-[var(--lilia-text-muted)]" />
            <input
              ref={inputRef}
              data-testid={testIds.liliaMenu.search}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={i18n.t("shell:menu.search")}
              aria-label={i18n.t("shell:bar.search")}
              className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[14px] text-[var(--lilia-text)] shadow-none ring-0 placeholder:text-[var(--lilia-text-muted)]"
              // Inline: the global focus ring is unlayered CSS, which beats
              // any utility class.
              style={{ outline: "none" }}
            />
          </label>
        </div>

        {typing ? (
          /* ── results, grouped ── */
          <div data-testid={testIds.liliaMenu.results} role="listbox" className="max-h-[420px] overflow-y-auto border-t border-[var(--lilia-border)] px-2.5 pb-2.5">
            {groups.length === 0 && (
              <div className="px-3 py-6 text-center text-[12.5px] text-[var(--lilia-text-muted)]">
                {docsQ.isFetching || q.trim() !== debounced ? i18n.t("shell:menu.searching") : i18n.t("shell:menu.nothingMatches", { q: q.trim() })}
              </div>
            )}
            {groups.map((g) => (
              <div key={g.id} data-testid={testIds.liliaMenu.group} data-group={g.id}>
                <div className={cn(sectionHead, "px-2 pb-1 pt-3")}>{g.label}</div>
                {g.items.map((r) => {
                  const i = flat.indexOf(r);
                  const on = i === active;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      role="option"
                      aria-selected={on}
                      data-testid={testIds.liliaMenu.result}
                      data-kind={r.kind}
                      data-id={r.id}
                      data-active={on || undefined}
                      onMouseMove={() => setActive(i)}
                      onClick={r.run}
                      className={cn("flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left", on && "bg-[var(--lilia-primary-subtle)]")}
                    >
                      <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[7px] bg-[var(--lilia-bg-secondary)] text-[var(--lilia-text-secondary)]">{r.icon}</span>
                      <span className="min-w-0 flex-1">
                        {/* Only the matched characters take the warm mark; the row's
                            hover and selection stay the cerulean wash (warm accent §1). */}
                        <span className="block truncate text-[13px] font-medium text-[var(--lilia-text)]"><SearchHitText text={r.label} query={q} /></span>
                        {r.hint && <span className="block truncate text-[11px] text-[var(--lilia-text-muted)]"><SearchHitText text={r.hint} query={q} /></span>}
                      </span>
                      {on && <span className="font-mono text-[11px] text-[var(--lilia-text-muted)]">↵</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        ) : page === "all" ? (
          /* ── All tools: every tool with its one line; pin or unpin ── */
          <div data-testid={testIds.liliaMenu.allTools} className="border-t border-[var(--lilia-border)] px-2.5 pb-2.5">
            <div className="flex items-center gap-1 px-1.5 pb-1 pt-2.5">
              <button type="button" data-menu-item data-testid={testIds.liliaMenu.back} onClick={() => setPage("main")} className={cn(itemCls, "inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[12px] font-medium text-[var(--lilia-primary)] hover:bg-[var(--lilia-hover)]")}>
                <IconChevronLeft size={14} /> {i18n.t("shell:menu.pinned")}
              </button>
              <span className={cn(sectionHead, "ml-2")}>{i18n.t("shell:menu.allTools")}</span>
            </div>
            {TOOLS.map((t) => {
              const isPinned = pinned.includes(t.key);
              return (
                <div key={t.key} data-testid={testIds.liliaMenu.tool} data-tool={t.key} className="flex items-center gap-2 rounded-lg px-1 hover:bg-[var(--lilia-hover)]">
                  <button type="button" data-menu-item onClick={() => openTool(t)} className={cn(itemCls, "flex min-w-0 flex-1 items-center gap-3 rounded-lg px-1.5 py-2 text-left")}>
                    <Glyph tool={t} on={t.key === current} size={28} />
                    <span className="min-w-0">
                      <span className="block text-[13px] font-semibold text-[var(--lilia-text)]">{t.name}</span>
                      <span className="block truncate text-[11.5px] text-[var(--lilia-text-muted)]">{t.desc}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    data-menu-item
                    data-testid={testIds.liliaMenu.toolPin}
                    data-pinned={isPinned}
                    onClick={() => togglePin(t.key)}
                    title={isPinned ? i18n.t("shell:menu.unpin") : i18n.t("shell:menu.pin")}
                    className={cn(itemCls, "flex h-8 w-8 items-center justify-center rounded-md text-[var(--lilia-text-muted)] hover:text-[var(--lilia-text)]")}
                  >
                    {isPinned ? <IconPinnedOff size={15} /> : <IconPin size={15} />}
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <>
            {/* Home, its own row: the way back to everything */}
            <div className="flex items-center gap-2.5 border-b border-[var(--lilia-border)] px-3.5 pb-3 pt-0.5">
              <button
                type="button"
                data-menu-item
                data-testid={testIds.liliaMenu.home}
                data-current={pathname === "/" || undefined}
                onClick={() => go(paths.home)}
                className={cn(itemCls, "flex h-[34px] flex-1 items-center gap-[9px] rounded-lg bg-[var(--lilia-bg-secondary)] px-3 text-left text-[13px] font-semibold text-[var(--lilia-text)] hover:bg-[var(--lilia-hover)]")}
              >
                <IconHome size={15} />
                {i18n.t("shell:home.label")}
                <span className="truncate text-[12px] font-normal text-[var(--lilia-text-muted)]">{i18n.t("shell:menu.homeHintLower")}</span>
              </button>
            </div>

            {/* Pinned */}
            <div className="flex items-baseline px-4 pb-1 pt-3">
              <span className={cn(sectionHead, "flex-1")}>{i18n.t("shell:menu.pinned")}</span>
              <button type="button" data-menu-item data-testid={testIds.liliaMenu.allToolsOpen} onClick={() => setPage("all")} className={cn(itemCls, "rounded text-[12px] font-medium text-[var(--lilia-primary)] hover:underline")}>
                {i18n.t("shell:menu.allToolsLink")}
              </button>
            </div>
            <div className="grid grid-cols-4 gap-1.5 px-3 pb-3 pt-1">
              {pinnedTools.map((t) => {
                const on = t.key === current;
                return (
                  <button
                    key={t.key}
                    type="button"
                    data-menu-item
                    data-menu-tile="1"
                    data-testid={testIds.liliaMenu.pinned(t.key)}
                    data-current={on || undefined}
                    draggable
                    onDragStart={(e) => { dragging.current = t.key; e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", t.key); }}
                    onDragOver={(e) => { if (dragging.current) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = dragging.current ?? (e.dataTransfer.getData("text/plain") as ToolKey);
                      dragging.current = null;
                      if (from) setPinned(movePinned(pinned, from, t.key));
                    }}
                    onDragEnd={() => { dragging.current = null; }}
                    onContextMenu={(e) => { e.preventDefault(); setPinned(pinned.filter((k) => k !== t.key)); }}
                    onClick={() => openTool(t)}
                    title={i18n.t("shell:menu.dragHint", { name: t.name })}
                    className={cn(
                      itemCls,
                      "flex h-[74px] min-w-0 flex-col items-center justify-center gap-[7px] rounded-[9px] transition-colors",
                      on ? "bg-[var(--lilia-primary-subtle)]" : "hover:bg-[var(--lilia-hover)]",
                    )}
                  >
                    <Glyph tool={t} on={on} />
                    <span className={cn("max-w-full truncate px-1 text-[12px] text-[var(--lilia-text)]", on && "font-semibold")}>{t.name}</span>
                  </button>
                );
              })}
              {pinnedTools.length === 0 && (
                <div className="col-span-4 px-2 py-4 text-center text-[12px] text-[var(--lilia-text-muted)]">
                  {i18n.t("shell:menu.nothingPinned")}
                </div>
              )}
            </div>

            {/* Recent: six items across tools, each opening straight into the item */}
            <div className={cn(sectionHead, "border-t border-[var(--lilia-border)] px-4 pb-1 pt-2.5")}>{i18n.t("shell:menu.recent")}</div>
            <div data-testid={testIds.liliaMenu.recent} className="grid grid-cols-2 gap-x-2 gap-y-0.5 px-2.5 pb-2.5 pt-1">
              {recentQ.isLoading && <div className="col-span-2 px-2 py-2 text-[12px] text-[var(--lilia-text-muted)]">{i18n.t("shell:menu.loading")}</div>}
              {!recentQ.isLoading && recent.length === 0 && (
                <div className="col-span-2 px-2 py-2 text-[12px] text-[var(--lilia-text-muted)]">{i18n.t("shell:menu.nothingYet")}</div>
              )}
              {recent.map((r) => {
                const t = toolByKey(r.tool);
                const Icon = t?.icon ?? IconFileText;
                return (
                  <button
                    key={`${r.tool}-${r.id}`}
                    type="button"
                    data-menu-item
                    data-testid={testIds.liliaMenu.recentItem}
                    data-tool={r.tool}
                    data-id={r.id}
                    onClick={() => go(itemHref(r.tool, r))}
                    className={cn(itemCls, "flex min-w-0 items-center gap-2.5 rounded-lg p-2 text-left hover:bg-[var(--lilia-hover)]")}
                  >
                    <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[7px] bg-[var(--lilia-bg-secondary)] text-[var(--lilia-text-secondary)]"><Icon size={14} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[12.5px] font-medium text-[var(--lilia-text)]">{r.title}</span>
                      <span className="block truncate text-[11px] text-[var(--lilia-text-muted)]">{t?.name ?? r.tool} · {relWhen(r.at)}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* footer */}
        <div className="flex h-[42px] items-center gap-[18px] border-t border-[var(--lilia-border)] bg-[var(--lilia-bg-secondary)] px-4 text-[12.5px] text-[var(--lilia-text-secondary)]">
          <button type="button" data-menu-item data-testid={testIds.liliaMenu.settings} onClick={() => openTool(toolByKey("settings")!)} className={cn(itemCls, "inline-flex items-center gap-1.5 rounded hover:text-[var(--lilia-text)]")}>
            <IconSettings size={14} /> {i18n.t("shell:tools.settings.name")}
          </button>
          <button type="button" data-menu-item data-testid={testIds.liliaMenu.trash} onClick={() => go(paths.trash)} className={cn(itemCls, "inline-flex items-center gap-1.5 rounded hover:text-[var(--lilia-text)]")}>
            <IconTrash size={14} /> {i18n.t("shell:menu.trash")}{trashCount ? ` · ${trashCount}` : ""}
          </button>
          <button type="button" data-menu-item data-testid={testIds.liliaMenu.help} onClick={() => go(paths.help())} className={cn(itemCls, "inline-flex items-center gap-1.5 rounded hover:text-[var(--lilia-text)]")}>
            <IconHelpCircle size={14} /> {i18n.t("shell:menu.help")}
          </button>
          <button type="button" data-menu-item data-testid={testIds.tour.takeTour} data-where={tourWhere} title={i18n.t(`editor:tour.${tourWhere}`)} onClick={() => { void takeTour(); }} className={cn(itemCls, "hidden items-center gap-1.5 rounded hover:text-[var(--lilia-text)] sm:inline-flex")}>
            <IconRoute size={14} /> {i18n.t("editor:tour.takeTour")}
          </button>
          <span className="flex-1" />
          <span className="hidden text-[11px] text-[var(--lilia-text-muted)] sm:inline">
            <Trans i18nKey="shell:menu.keys" components={{ b: <b className="font-mono" /> }} />
          </span>
        </div>
      </div>
    </>
  );
}
